import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, Compass } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';

const Login = ({ setUser, setNeedsPasswordChange }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { colors, mode } = useTheme();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        if (data.needs_password_change && setNeedsPasswordChange) {
          setNeedsPasswordChange(true);
        }
        setUser(data.user);
        toast.success(`Welcome back, ${data.user.name}!`);

        // Small delay to let React re-render with new user state before navigating
        const defaultRoutes = {
          youth: '/dashboard', coach: '/coach', lead_monitor: '/lead-monitor',
          parent: '/parent', admin: '/admin', program_admin: '/admin', platform_admin: '/platform'
        };
        const targetRoute = defaultRoutes[data.user.role] || '/dashboard';
        setTimeout(() => navigate(targetRoute), 100);
      } else {
        toast.error(data.detail || 'Invalid email or password');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #2A4470 50%, #1B2D4F 100%)' }}>
      {/* Background decoration */}
      <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute bottom-20 -right-20 w-72 h-72 rounded-full bg-teal-500/10 blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-indigo-500/5 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <div className="rounded-3xl shadow-2xl p-8 md:p-10 backdrop-blur-sm" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }} className="mb-4">
              <img src="/yas-connect-logo.png" alt="YAS Connect" className="h-20 mx-auto" />
            </motion.div>
            <p className="text-gray-500 text-sm">Sign in to continue your journey</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div data-testid="email-field">
              <Label htmlFor="email" className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5 block">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="email" type="email" data-testid="email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="pl-11 h-12 rounded-xl bg-gray-50 border-gray-200 text-gray-900 placeholder:text-white/40 focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                  required
                />
              </div>
            </div>

            <div data-testid="password-field">
              <Label htmlFor="password" className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="password" type="password" data-testid="password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-11 h-12 rounded-xl bg-gray-50 border-gray-200 text-gray-900 placeholder:text-white/40 focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                  required
                />
              </div>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-orange-400 hover:text-orange-300 transition-colors">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" data-testid="login-button" disabled={loading}
              className="w-full h-12 rounded-xl font-bold text-base transition-all active:scale-[0.98] shadow-lg shadow-orange-500/25"
              style={{ background: 'linear-gradient(135deg, #E86C3D, #C4542D)' }}>
              {loading ? 'Signing in...' : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Don't have an account?{' '}
              <Link to="/register" data-testid="register-link" className="font-semibold text-orange-400 hover:text-orange-300 transition-colors">Sign up</Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/how-to-use" data-testid="how-to-use-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors">
              <Compass className="w-4 h-4" /> New here? Learn how to use the platform
            </Link>
          </div>
        </div>

        <p className="text-center mt-4 text-xs text-white/40">YAS Connect — Youth Achievement & Support</p>
      </motion.div>
    </div>
  );
};

export default Login;
